import { useEffect } from "react";

import { Authenticated, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

// router
import routerBindings, {
  CatchAllNavigate,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { BrowserRouter, Route, Routes, Outlet } from "react-router-dom";

// refine mui components
import {
  ErrorComponent,
  notificationProvider,
  RefineSnackbarProvider,
  ThemedLayoutV2,
  ThemedTitleV2,
} from "@refinedev/mui";

// mui styles
import GlobalStyles from "@mui/material/GlobalStyles";
import CssBaseline from "@mui/material/CssBaseline";

// styles
import { ColorModeContextProvider } from "./contexts/color-mode";

// pages
import { FormCreate, FormList, FormShow } from "pages/forms";
import { UserCreate, UserList, UserEdit } from "pages/users";
import { RecordList } from "pages/records";
import { LoginPage } from "components/pages/auth/components";
import { ResponseList } from "pages/responses";

// utils
import { authProvider } from "utils/authProvider";
import { checkLocation } from "utils/location";
import { API_URL } from "utils/env";
import { axiosInstance } from "rest-data-provider/utils";
import { dataProvider } from "rest-data-provider";
import { QueryClient } from "@tanstack/react-query";

// keys
import { QueryKeys } from "constants/keys";
import { UserEntity, UserEntityRoleEnum } from "generated/api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  },
});

function App() {
  useEffect(() => {
    checkLocation();
  }, []);

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <CssBaseline />
          <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
          <RefineSnackbarProvider>
            <Refine
              notificationProvider={notificationProvider}
              routerProvider={routerBindings}
              authProvider={authProvider}
              accessControlProvider={{
                can: async ({ resource }) => {
                  const querydata = queryClient.getQueryData([
                    QueryKeys.ME_QUERY,
                  ]) as UserEntity | undefined;

                  let isAdmin = false;
                  if (querydata) {
                    isAdmin = querydata.role === UserEntityRoleEnum.Admin;
                  }
                  if (resource === "users" && !isAdmin) {
                    return {
                      can: false,
                      reason: "Unauthorized",
                    };
                  }
                  return {
                    can: true,
                  };
                },
              }}
              dataProvider={dataProvider(
                API_URL,
                //@ts-ignore
                axiosInstance
              )}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                reactQuery: {
                  clientConfig: queryClient,
                },
              }}
              resources={[
                {
                  name: "forms",
                  list: "/forms",
                  create: "/forms/create",
                  edit: "/forms/edit/:id",
                  show: "/forms/show/:id",
                },
                {
                  name: "users",
                  list: "/users",
                  edit: "/users/edit/:id",
                  create: "/users/create",
                },
                {
                  name: "records",
                  list: "/records/:formId",

                  meta: {
                    hide: true,
                  },
                },
                {
                  name: "responses",
                  list: "/responses/:formId",

                  meta: {
                    hide: true,
                  },
                },
              ]}
            >
              <Routes>
                <Route
                  element={
                    <Authenticated fallback={<CatchAllNavigate to="/login" />}>
                      <ThemedLayoutV2
                        Title={({ collapsed }) => (
                          <ThemedTitleV2
                            // collapsed is a boolean value that indicates whether the <Sidebar> is collapsed or not
                            collapsed={collapsed}
                            text={"Ward 10 profile"}
                            icon={
                              <image
                                href={require("assets/images/ward10_icon.png")}
                                height="24"
                                width="24"
                              />
                            }
                          />
                        )}
                      >
                        <Outlet />
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  <Route
                    index
                    element={<NavigateToResource resource="forms" />}
                  />

                  <Route path="forms">
                    <Route index element={<FormList />} />
                    <Route path="create" element={<FormCreate />} />
                    <Route path="edit/:id" element={<FormCreate />} />
                    <Route path="show/:id" element={<FormShow />} />
                  </Route>

                  <Route path="users">
                    <Route index element={<UserList />} />
                    <Route path="edit/:id" element={<UserEdit />} />
                    <Route path="create" element={<UserCreate />} />
                  </Route>

                  <Route path="records">
                    <Route path=":formId" element={<RecordList />} />
                  </Route>

                  <Route path="responses">
                    <Route path=":formId" element={<ResponseList />} />
                  </Route>
                </Route>

                <Route
                  element={
                    <Authenticated fallback={<Outlet />}>
                      <NavigateToResource />
                    </Authenticated>
                  }
                >
                  <Route
                    path="/login"
                    element={
                      <LoginPage
                        title={
                          <ThemedTitleV2 collapsed={false} text="" icon={""} />
                        }
                      />
                    }
                  />
                </Route>
                <Route
                  element={
                    <Authenticated fallback={<Outlet />}>
                      <ThemedLayoutV2>
                        <Outlet />
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Routes>
              <RefineKbar />
              <UnsavedChangesNotifier />
            </Refine>
          </RefineSnackbarProvider>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
